import Api from "@/api/endpoints";
import { STORAGE_KEYS } from "@/constants";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

const getStoredToken = (key) => {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

// Async thunks for authentication
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await Api.auth.login(credentials);

      // Backend returns: { success: true, data: { user, token, expiresIn }, meta: {...} }
      const { user, token } = response.data;

      // Clear super admin token when normal user logs in
      if (typeof window !== "undefined") {
        localStorage.removeItem("superAdminToken");
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
        localStorage.removeItem("tally_selected_company_id");
      }
      return { user, token };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  }
);

export const mobileLogin = createAsyncThunk(
  "auth/mobileLogin",
  async (mobileData, { rejectWithValue }) => {
    try {
      const response = await Api.auth.mobileLogin(mobileData);

      // For new users, we don't get a token yet
      if (response.data.isNewUser) {
        return { user: null, token: null, isNewUser: true };
      }

      // For existing users, we get a token
      const { user, token } = response.data;
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      return { user, token, isNewUser: false };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Mobile login failed");
    }
  }
);

export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (userData, { rejectWithValue }) => {
    try {
      // Transform frontend data to backend format
      const registrationData = {
        user_name: `${userData.firstName} ${userData.lastName}`.trim(),
        password_plain: userData.password,
        mobile_number: userData.mobile,
        is_active: true,
      };

      const response = await Api.auth.register(registrationData);
      // Registration doesn't return token, so we need to login after registration
      if (response.success) {
        // Auto-login after successful registration
        const loginResponse = await Api.auth.login({
          mobile_number: userData.mobile,
          password: userData.password,
        });
        // Backend returns: { success: true, data: { user, token, expiresIn }, meta: {...} }
        const { user, token } = loginResponse.data;
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
        return { user, token };
      }
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Registration failed");
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  "auth/getCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Api.auth.me();

      // Backend returns: { success: true, data: { user_data }, meta: {...} }
      const userData = response.data;

      // Ensure referral code is included in the user data
      if (userData && !userData.referral_code) {
        userData.referral_code = null;
      }

      return userData;
    } catch (error) {
      // Pass error with status code for proper handling
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to get user",
        statusCode: error.response?.status,
        response: error.response,
      });
    }
  }
);

export const logoutUser = createAsyncThunk("auth/logoutUser", async () => {
  if (typeof window !== "undefined") {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.COMPANY_DATA);
    localStorage.removeItem("tally_selected_company_id");
  }
  return null;
});

const extractSuperAdminPayload = (response) => {
  if (!response) return {};
  if (response.data && typeof response.data === "object" && Object.keys(response).length <= 3) {
    return extractSuperAdminPayload(response.data);
  }
  return response;
};

export const superAdminRegister = createAsyncThunk(
  "auth/superAdminRegister",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Api.superAdmin.register(payload);
      const data = extractSuperAdminPayload(response);
      const token = data.token || data.accessToken || data?.data?.token || null;
      const user = data.user || data?.data?.user || null;
      if (token && typeof window !== "undefined") {
        localStorage.setItem("superAdminToken", token);
      }
      return { token, user };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Super Admin registration failed");
    }
  }
);

export const superAdminLogin = createAsyncThunk(
  "auth/superAdminLogin",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await Api.superAdmin.login(credentials);
      const data = extractSuperAdminPayload(response);
      const token = data.token || data.accessToken || data?.data?.token || null;
      const user = data.user || data?.data?.user || null;
      if (!token) {
        throw new Error("Missing token in Super Admin login response");
      }
      // Clear normal user token when super admin logs in
      if (typeof window !== "undefined") {
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        localStorage.setItem("superAdminToken", token);
        localStorage.removeItem("tally_selected_company_id");
      }
      return { token, user };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Super Admin login failed"
      );
    }
  }
);

// OTP Async Thunks
export const sendOtp = createAsyncThunk("auth/sendOtp", async (mobileData, { rejectWithValue }) => {
  try {
    const sendData = { mobile_number: mobileData.mobile_number };
    const response = await Api.users.sendOtp(sendData);

    const userExists =
      response.data?.data?.userExists === true
        ? true
        : response.data?.data?.userExists === false
          ? false
          : response.data?.userExists === true
            ? true
            : response.data?.userExists === false
              ? false
              : null;

    const otpValue =
      response.data?.data?.otp || // Standard nested structure
      response.data?.otp || // Direct in data
      response?.data?.data?.otp || // Double nested
      null;

    return {
      success: response.data.success,
      message: response.data.message,
      userExists: userExists,
      otp: otpValue,
    };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to send OTP");
  }
});

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (mobileData, { rejectWithValue }) => {
    try {
      const response = await Api.users.resendOtp({
        mobile_number: mobileData.mobile_number,
      });

      const data = response.data?.data;
      const userExists =
        data?.userExists === true
          ? true
          : data?.userExists === false
            ? false
            : response.data?.userExists === true
              ? true
              : response.data?.userExists === false
                ? false
                : null;

      const otpValue = response.data?.data?.otp || response.data?.otp || null;

      return {
        success: response.data.success,
        userExists: userExists,
        otp: otpValue,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to resend OTP");
    }
  }
);
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async (otpData, { rejectWithValue }) => {
    try {
      const response = await Api.users.verifyOtp({
        mobile_number: otpData.mobile_number,
        otp: otpData.otp,
      });

      // Response structure:
      // Backend returns: { success, message, data: { userExists, token, user }, meta }
      // axiosClient.get/post returns: axios response object { data: {...}, status, headers, ... }
      // http.post extracts: r.data, so response = { success, message, data: { userExists, token, user }, meta }
      // Therefore: response.data = { userExists, token, user }

      // Handle both possible response structures
      let responseData;
      let responseMessage;

      // Check if response has nested data structure
      if (response.data && typeof response.data === "object" && "userExists" in response.data) {
        // Direct structure: response.data = { userExists, token, user }
        responseData = response.data;
        responseMessage = response.message || "";
      } else if (response.data && response.data.data) {
        // Nested structure: response.data = { success, message, data: { userExists, token, user } }
        responseData = response.data.data;
        responseMessage = response.data.message || response.message || "";
      } else {
        // Fallback: try response directly
        responseData = response.data || response;
        responseMessage = response.message || "";
      }

      // Extract token and userExists from the response
      const token = responseData?.token || null;

      // Extract userExists - backend explicitly returns true (user exists) or false (new user)
      // Backend returns: userExists: true for existing users, userExists: false for new users
      // IMPORTANT: userExists must be explicitly checked, not just truthy/falsy
      let userExists = responseData?.userExists;


      // Fallback: check response message if userExists is undefined/null
      if (userExists === undefined || userExists === null) {
        const isNewUserMessage = responseMessage.toLowerCase().includes("new user");
        userExists = isNewUserMessage ? false : true;
      }

      // Ensure userExists is explicitly true or false
      // userExists === true means user exists in database (existing user) → redirect to dashboard
      // userExists === false means user doesn't exist in database (new user) → redirect to role-select
      userExists = userExists === true ? true : false;

      if (!token) {
        return rejectWithValue("Authentication failed: No token received");
      }

      // Just return the data, let the component handle navigation
      return {
        success: response.success || true,
        token,
        userExists,
        user: responseData.user || null, // ✅ Include user data with referral code
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to verify OTP");
    }
  }
);

// After OTP verification, check if user exists using mobile login endpoint

// Forgot Password and Reset Password Async Thunks
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (emailData, { rejectWithValue }) => {
    try {
      const response = await Api.users.forgotPassword(emailData);
      return response;
    } catch (error) {
      const apiErr = error.response?.data;
      const detailMsg = apiErr?.error?.details?.[0]?.message;
      return rejectWithValue(detailMsg || apiErr?.message || "Failed to initiate password reset");
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (resetData, { rejectWithValue }) => {
    try {
      const response = await Api.users.resetPassword(resetData);
      return response;
    } catch (error) {
      const apiErr = error.response?.data;
      const detailMsg = apiErr?.error?.details?.[0]?.message;
      return rejectWithValue(detailMsg || apiErr?.message || "Failed to reset password");
    }
  }
);

const initialSuperAdminToken = getStoredToken("superAdminToken");

const getInitialToken = () =>
  getStoredToken(STORAGE_KEYS.AUTH_TOKEN) || getStoredToken("auth_token");

const initialState = {
  user: null,
  token: getInitialToken(),
  // Treat presence of token as authenticated on app start to avoid redirect on refresh
  isAuthenticated: !!getInitialToken(),
  onboarding_step: 1, // Default to step 1 (role-select)
  // If a token exists, start in loading state to hydrate current user
  isLoading: !!getInitialToken(),
  error: null,
  // OTP states
  otpSent: false,
  otpVerified: false,
  otpLoading: false,
  otpError: null,
  userExists: null,
  currentOtp: null,
  // Forgot Password states
  forgotPasswordLoading: false,
  forgotPasswordSuccess: false,
  forgotPasswordError: null,
  resetPasswordLoading: false,
  resetPasswordSuccess: false,
  resetPasswordError: null,
  superAdmin: {
    user: null,
    token: initialSuperAdminToken,
    isAuthenticated: !!initialSuperAdminToken,
    isLoading: false,
    error: null,
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action) => {
      const { user, token } = action.payload;

      if (token !== undefined) {
        state.token = token;
        state.isAuthenticated = !!token;
        if (token) {
          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
        } else {
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        }
      }

      if (user !== undefined) {
        state.user = user;
        state.onboarding_step = user?.onboarding_step ?? 1;
      }
    },
    setToken: (state, action) => {
      const token = action.payload;
      state.token = token;
      state.isAuthenticated = !!token;

      // Only update localStorage if we have a token
      if (token) {
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      } else {
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      }
    },
    clearOtpState: (state) => {
      state.otpSent = false;
      state.otpVerified = false;
      state.otpError = null;
      state.currentOtp = null;
    },
    setCustomError: (state, action) => {
      state.error = action.payload;
    },
    clearForgotPasswordState: (state) => {
      state.forgotPasswordLoading = false;
      state.forgotPasswordSuccess = false;
      state.forgotPasswordError = null;
    },
    clearResetPasswordState: (state) => {
      state.resetPasswordLoading = false;
      state.resetPasswordSuccess = false;
      state.resetPasswordError = null;
    },
    logoutSuperAdmin: (state) => {
      state.superAdmin.user = null;
      state.superAdmin.token = null;
      state.superAdmin.isAuthenticated = false;
      state.superAdmin.error = null;
      if (typeof window !== "undefined") {
        localStorage.removeItem("superAdminToken");
      }
    },
    setSuperAdminProfile: (state, action) => {
      state.superAdmin.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.error = null;
        state.onboarding_step = action.payload.user?.onboarding_step ?? 1;
        // Clear super admin state when normal user logs in
        state.superAdmin.token = null;
        state.superAdmin.user = null;
        state.superAdmin.isAuthenticated = false;
        if (typeof window !== "undefined") {
          localStorage.removeItem("superAdminToken");
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })

      // Mobile Login
      .addCase(mobileLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(mobileLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = !!action.payload.token; // Only authenticated if we have a token
        state.error = null;
      })
      .addCase(mobileLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.onboarding_step = action.payload?.onboarding_step ?? 1;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        // Check if it's a 401 error - this means token is invalid
        // Error can be in different formats: string, object with statusCode, or object with response.status
        const errorPayload = action.payload;
        let errorStatus = null;

        if (typeof errorPayload === "object") {
          errorStatus =
            errorPayload?.statusCode || errorPayload?.response?.status || errorPayload?.status;
        }

        if (errorStatus === 401) {
          // Token is invalid, clear it
          state.token = null;
          state.isAuthenticated = false;
          state.user = null;
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          state.error = "Session expired. Please login again.";
        } else {
          // Keep token and authenticated flag for other errors (network, etc.)
          state.error =
            typeof errorPayload === "string"
              ? errorPayload
              : errorPayload?.message || "Failed to restore session";
        }
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
        state.onboarding_step = 1;
        // Clear OTP-related states
        state.otpSent = false;
        state.otpVerified = false;
        state.otpLoading = false;
        state.otpError = null;
        state.userExists = null;
        state.currentOtp = null;
      })
      // Send OTP
      .addCase(sendOtp.pending, (state) => {
        state.otpLoading = true;
        state.otpError = null;
      })
      .addCase(sendOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.otpSent = true;
        state.otpError = null;
        state.userExists = action.payload.userExists;
        state.currentOtp = action.payload.otp; // Store OTP for dev/testing
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.otpError = action.payload;
      })
      // Resend OTP
      .addCase(resendOtp.pending, (state) => {
        state.otpLoading = true;
        state.otpError = null;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.otpSent = true;
        state.otpError = null;
        // Set userExists from the response
        state.userExists = action.payload.userExists;
        state.currentOtp = action.payload.otp; // Store OTP for dev/testing
        if (action.payload.token) {
          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, action.payload.token);
          state.token = action.payload.token;
          state.isAuthenticated = true;
        }
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.otpError = action.payload;
      })
      // Verify OTP
      .addCase(verifyOtp.pending, (state) => {
        state.otpLoading = true;
        state.otpError = null;
        state.otpVerified = false;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
        state.otpVerified = true;
        state.otpError = null;

        const { token, userExists, user } = action.payload;

        if (token) {
          state.token = token;
          state.isAuthenticated = true;
          state.userExists = userExists;

          // Clear super admin state when normal user logs in via OTP
          state.superAdmin.token = null;
          state.superAdmin.user = null;
          state.superAdmin.isAuthenticated = false;
          if (typeof window !== "undefined") {
            localStorage.removeItem("superAdminToken");
          }

          // Set user data - for new users, user object should be provided by backend
          if (user) {
            state.user = user;
            state.onboarding_step = user.onboarding_step ?? 1;
          }

          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
        } else {
          state.otpError = "Authentication failed: No token received";
        }
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.otpVerified = false;
        state.otpError = action.payload || "Failed to verify OTP";
      })
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPasswordLoading = true;
        state.forgotPasswordSuccess = false;
        state.forgotPasswordError = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordSuccess = true;
        state.forgotPasswordError = null;
      })
      .addCase(forgotPassword.rejected, (state) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordSuccess = true; // Still show success for security
        state.forgotPasswordError = null;
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.resetPasswordLoading = true;
        state.resetPasswordSuccess = false;
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.resetPasswordLoading = false;
        state.resetPasswordSuccess = true;
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordSuccess = false;
        state.resetPasswordError = action.payload;
      })
      // Super Admin Register
      .addCase(superAdminRegister.pending, (state) => {
        state.superAdmin.isLoading = true;
        state.superAdmin.error = null;
      })
      .addCase(superAdminRegister.fulfilled, (state, action) => {
        state.superAdmin.isLoading = false;
        state.superAdmin.token = action.payload.token || null;
        state.superAdmin.user = action.payload.user || null;
        state.superAdmin.isAuthenticated = !!action.payload.token;
        if (action.payload.token && typeof window !== "undefined") {
          localStorage.setItem("superAdminToken", action.payload.token);
        }
      })
      .addCase(superAdminRegister.rejected, (state, action) => {
        state.superAdmin.isLoading = false;
        state.superAdmin.error = action.payload;
        state.superAdmin.isAuthenticated = false;
        state.superAdmin.token = null;
      })
      // Super Admin Login
      .addCase(superAdminLogin.pending, (state) => {
        state.superAdmin.isLoading = true;
        state.superAdmin.error = null;
      })
      .addCase(superAdminLogin.fulfilled, (state, action) => {
        state.superAdmin.isLoading = false;
        state.superAdmin.token = action.payload.token;
        state.superAdmin.user = action.payload.user || state.superAdmin.user;
        state.superAdmin.isAuthenticated = true;
        // Clear normal user state when super admin logs in
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        if (typeof window !== "undefined") {
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.setItem("superAdminToken", action.payload.token);
        }
      })
      .addCase(superAdminLogin.rejected, (state, action) => {
        state.superAdmin.isLoading = false;
        state.superAdmin.error = action.payload;
        state.superAdmin.isAuthenticated = false;
        state.superAdmin.token = null;
        if (typeof window !== "undefined") {
          localStorage.removeItem("superAdminToken");
        }
      });
  },
});

export const {
  clearError,
  setCredentials,
  clearForgotPasswordState,
  clearOtpState,
  setCustomError,
  setToken,
  clearResetPasswordState,
  logoutSuperAdmin,
  setSuperAdminProfile,
} = authSlice.actions;
export default authSlice.reducer;
